import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import PublicViewPreviewContent from "./PublicViewPreviewContent";
import {
  FONT_AWESOME_URL,
  PUBLIC_VIEW_STYLES_URL,
  fetchPublicViewCssText,
  readHostCssVars,
  DEFAULT_PREVIEW_SECTION,
} from "./publicViewThemeUtils";

function applyThemeVarsToHost(host, themeVars, clearedCssVars) {
  if (!host) return;

  clearedCssVars.forEach((cssVar) => {
    host.style.removeProperty(cssVar);
  });

  Object.entries(themeVars).forEach(([cssVar, value]) => {
    if (clearedCssVars.has(cssVar)) return;
    if (value) host.style.setProperty(cssVar, value);
  });
}

function mountPreviewContent(
  shadowRoot,
  host,
  {
    auctionDetail,
    previewSection,
    previewContextSource,
    emphasizePreviewRegions,
    themeVars,
    clearedCssVars,
    onCssVarsReady,
    cssVarsNotifiedRef,
    mountRef,
  },
) {
  if (mountRef.current) return;

  const mountPoint = document.createElement("div");
  mountPoint.classList.add("cuspre", "body");
  shadowRoot.appendChild(mountPoint);
  mountRef.current = mountPoint;

  ReactDOM.render(
    <PublicViewPreviewContent
      auctionDetail={auctionDetail}
      previewSection={previewSection}
      previewContextSource={previewContextSource}
      emphasizePreviewRegions={emphasizePreviewRegions}
    />,
    mountPoint,
  );

  if (!cssVarsNotifiedRef.current && onCssVarsReady) {
    cssVarsNotifiedRef.current = true;
    requestAnimationFrame(() => {
      onCssVarsReady(readHostCssVars(host));
    });
  }

  applyThemeVarsToHost(host, themeVars, clearedCssVars);
}

function appendFontAwesomeLink(shadowRoot) {
  if (shadowRoot.querySelector('link[data-pv-font-awesome="true"]')) return;

  const faLink = document.createElement("link");
  faLink.rel = "stylesheet";
  faLink.href = FONT_AWESOME_URL;
  faLink.dataset.pvFontAwesome = "true";
  shadowRoot.appendChild(faLink);
}

function appendStylesheetLink(shadowRoot, onLoad, onError) {
  if (shadowRoot.querySelector('link[data-pv-remote-styles="true"]')) return;

  const stylesLink = document.createElement("link");
  stylesLink.rel = "stylesheet";
  stylesLink.href = PUBLIC_VIEW_STYLES_URL;
  stylesLink.dataset.pvRemoteStyles = "true";
  stylesLink.addEventListener("load", onLoad);
  stylesLink.addEventListener("error", onError);
  shadowRoot.appendChild(stylesLink);

  if (stylesLink.sheet) {
    onLoad();
  }
}

export default function PublicViewPreview({
  auctionDetail,
  themeVars = {},
  clearedCssVars = new Set(),
  previewSection = DEFAULT_PREVIEW_SECTION,
  previewContextSource = "currentPlayer",
  emphasizePreviewRegions = false,
  onCssVarsReady,
}) {
  const hostRef = useRef(null);
  const mountRef = useRef(null);
  const cssVarsNotifiedRef = useRef(false);
  const themeVarsRef = useRef(themeVars);
  const clearedCssVarsRef = useRef(clearedCssVars);

  themeVarsRef.current = themeVars;
  clearedCssVarsRef.current = clearedCssVars;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const shadowRoot =
      host.shadowRoot ??
      host.attachShadow({
        mode: "open",
      });

    if (mountRef.current) return;

    let cancelled = false;

    appendFontAwesomeLink(shadowRoot);

    const mountOptions = () => ({
      auctionDetail,
      previewSection,
      previewContextSource,
      emphasizePreviewRegions,
      themeVars: themeVarsRef.current,
      clearedCssVars: clearedCssVarsRef.current,
      onCssVarsReady,
      cssVarsNotifiedRef,
      mountRef,
    });

    const mountPreview = () => {
      if (cancelled || mountRef.current) return;
      mountPreviewContent(shadowRoot, host, mountOptions());
    };

    const mountWithStylesheetLink = () => {
      appendStylesheetLink(
        shadowRoot,
        mountPreview,
        () => console.error("Failed to load public view stylesheet"),
      );
    };

    if (!shadowRoot.querySelector("#pv-remote-styles")) {
      fetchPublicViewCssText()
        .then((cssText) => {
          if (cancelled || mountRef.current) return;

          const styleEl = document.createElement("style");
          styleEl.id = "pv-remote-styles";
          styleEl.textContent = cssText;
          shadowRoot.appendChild(styleEl);

          mountPreview();
        })
        .catch((error) => {
          console.error(
            "Failed to fetch public view stylesheet, falling back to link tag",
            error,
          );
          if (!cancelled) {
            mountWithStylesheetLink();
          }
        });
    } else {
      mountPreview();
    }

    return () => {
      cancelled = true;
    };
  }, [onCssVarsReady]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    ReactDOM.render(
      <PublicViewPreviewContent
        auctionDetail={auctionDetail}
        previewSection={previewSection}
        previewContextSource={previewContextSource}
        emphasizePreviewRegions={emphasizePreviewRegions}
      />,
      mount,
    );
  }, [auctionDetail, previewSection, previewContextSource, emphasizePreviewRegions]);

  // Same pipeline for colors AND --bg-img on preview host (:host overrides styles.css)
  useEffect(() => {
    applyThemeVarsToHost(hostRef.current, themeVars, clearedCssVars);
  }, [themeVars, clearedCssVars]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host?.shadowRoot) return;

    const name = auctionDetail?.detail?.auctionname;
    if (name) {
      const h1 = host.shadowRoot.querySelector(
        ".player-section .section-header h1",
      );
      if (h1) h1.textContent = name;
    }

    const logoPath =
      auctionDetail?.path && auctionDetail?.detail?.logo
        ? auctionDetail.path + auctionDetail.detail.logo
        : null;
    if (logoPath) {
      const leagueImg = host.shadowRoot.querySelector(
        ".player-section .league-logo img",
      );
      if (leagueImg) leagueImg.setAttribute("src", logoPath);
    }

    if (auctionDetail?.spn?.[0]?.logo && auctionDetail?.fpath) {
      const spnImg = host.shadowRoot.querySelector(
        ".player-section .sponsor-logo img",
      );
      if (spnImg)
        spnImg.setAttribute(
          "src",
          auctionDetail.fpath + auctionDetail.spn[0].logo,
        );
    }
  }, [auctionDetail]);

  return <div className="custom_preview" ref={hostRef}></div>;
}
